import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Route, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { BreadCrumb } from '../../models/common.models';
import { ConfigurationFacade } from '../../facades/configuration.facade';
import { ERouteDataTypes } from '../../configurations/common';
import { ESeoData, ETitleSources } from '../../configurations/seo';
import { SeoFacade } from '../../facades/seo.facade';
import { BREADCRUMB_SCHEME_CLASS } from '../../configurations/structured-data';
import { I18nService } from '../../services';
import { StringUtils } from '../../utils/string.utils';
import { firstValueFrom, Subject } from 'rxjs';
import { LocalStorageUtils } from '../../utils/localStorage.utils';

@Component({
  selector: 'app-breadcrumbs',
  templateUrl: './breadcrumbs.component.html',
  styleUrls: ['./breadcrumbs.component.scss'],
  standalone: false,
})

export class BreadcrumbsComponent implements OnInit, OnDestroy {
  currentRoute!: NavigationEnd;
  breadcrumbs!: BreadCrumb[];
  breadcrumbsUpdating: boolean = false;
  displayBreadCrumbs: boolean = false;
  breadcrumbsLoading: boolean = true;
  isBreadcrumbTitleSource: boolean = false;
  breadcrumb!: BreadCrumb;

  private readonly unsubscribe$: Subject<void> = new Subject<void>();

  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);
  private readonly configFacade: ConfigurationFacade = inject(ConfigurationFacade);
  private readonly seoFacade: SeoFacade = inject(SeoFacade);
  private readonly i18nService: I18nService = inject(I18nService);

  ngOnInit(): void {
    this.breadcrumb = this.getHomePageForBreadcrumbs();
    this.setBreadcrumbs();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  setBreadcrumbs(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(async (route) => {
      this.currentRoute = route as NavigationEnd;
      this.displayBreadCrumbs = false;
      this.breadcrumbsLoading = true;
      const root: ActivatedRoute = this.route.root;
      this.breadcrumbs = [this.getHomePageForBreadcrumbs(), ...this.configFacade.getBreadcrumbs(root)];
      const hasBreadcrumbs: boolean = this.configFacade.getBreadcrumbsParam(this.route, ERouteDataTypes.SHOW_DATA_BREADCRUMB) as boolean;
      this.displayBreadCrumbs = hasBreadcrumbs;
      this.isBreadcrumbTitleSource =
        this.configFacade.getBreadcrumbsParam(this.route, ESeoData.seoTitleSource) === ETitleSources.BREADCRUMBS;
      if (hasBreadcrumbs) {
        this.updateBreadcrumbsParams(this.breadcrumbs, route as NavigationEnd).then(() => {
          if (this.currentRoute === route) {
            this.displayBreadCrumbs = this.breadcrumbs.length > 1 ? hasBreadcrumbs : false;
            this.breadcrumbsLoading = this.breadcrumbsUpdating;
            if (this.isBreadcrumbTitleSource) {
              this.setTitleFromBreadcrumbs();
            }
          }
        });
      } else {
        this.seoFacade.removeStructuredData(BREADCRUMB_SCHEME_CLASS);
      }
    });
  }

  async updateBreadcrumbsParams(breadcrumbs: BreadCrumb[], route: NavigationEnd): Promise<void> {
    this.breadcrumbsUpdating = true;
    if (this.currentRoute === route) {
      for (const breadcrumb of breadcrumbs) {
        switch (breadcrumb.param) {
          case 'translateKey':
            await this.translateBreadcrumb(breadcrumbs, breadcrumb, route);
            break;
          case 'sku':
            await this.updateBreadcrumbWithCategoryTree(breadcrumbs, breadcrumb, route);
            break;
          default:
            this.updateBreadcrumb(breadcrumbs, route);
            break;
        }
      }
    } else {
      this.breadcrumbsUpdating = false;
    }
  }

  async translateBreadcrumb(breadcrumbs: BreadCrumb[], breadcrumb: BreadCrumb, route: NavigationEnd): Promise<void> {
    this.breadcrumbsUpdating = true;
    await firstValueFrom(this.configFacade.getTranslationByKey(breadcrumb.label)).then(
      (val) => {
        if (this.currentRoute === route) {
          breadcrumb.label = val;
          this.breadcrumbs = [...breadcrumbs];
        }
        this.breadcrumbsUpdating = false;
      }
    );
  }

  async updateBreadcrumbWithCategoryTree(breadcrumbs: BreadCrumb[], breadcrumb: BreadCrumb, route: NavigationEnd): Promise<void> {
    if (breadcrumb.label.match(/A_[a-zA-Z0-9]/)) {
      this.breadcrumbsUpdating = true;
      await this.configFacade.getProductWithCategoryTree(breadcrumb.label)
        .then(response => {
          if (this.currentRoute === route) {
            const categoryTree: Record<string, Record<string, unknown>>[] = response.included
              .filter((category: Record<string, unknown>) => (category['id'] as number) > 2)
              .sort((a: Record<string, unknown>, b: Record<string, unknown>) => +(b['id'] as number) - (+(a['id'] as number)))
              .reverse();
            const productName: string = response.data.attributes.name;
            const catBreadcrumb: BreadCrumb[] = this.getOneFromMultiCategoryBreadcrumbs(
              this.getMultiCategoriesBreadcrumbs(categoryTree)
            );
            breadcrumb.label = productName;
            this.breadcrumbs = breadcrumbs.slice(0, 1).concat(catBreadcrumb, breadcrumbs.slice(1));
          }
          this.breadcrumbsUpdating = false;
        })
        .catch(() => {
          this.breadcrumbsUpdating = false;
        });
    }
  }

  getMultiCategoriesBreadcrumbs(categoryTree: Record<string, Record<string, unknown>>[]): BreadCrumb[][] {
    const multiCategoryBreadcrumbsOptions: BreadCrumb[][] = [];
    categoryTree.forEach((category: Record<string, Record<string, unknown>>) => {
      let categoryIsUsed: boolean = false;
      let lastUsedNodeId: number = 0;
      multiCategoryBreadcrumbsOptions.forEach((option: BreadCrumb[], index: number) => {
        option.forEach((catBreadcrumb: BreadCrumb, i: number) => {
          if (catBreadcrumb.children?.find((child: { nodeId: number }) => child.nodeId === category['attributes']?.['nodeId']) !== undefined) {
            if (i + 1 === option.length) {
              multiCategoryBreadcrumbsOptions[index].push(this.createCategoryBreadcrumb(category));
            } else if (lastUsedNodeId != (catBreadcrumb as BreadCrumb & { nodeId: number }).nodeId) {
              const newCatBreadcrumb: BreadCrumb[] = option.slice(0, i + 1);
              newCatBreadcrumb.push(this.createCategoryBreadcrumb(category));
              multiCategoryBreadcrumbsOptions.push(newCatBreadcrumb);
            }
            lastUsedNodeId = (catBreadcrumb as BreadCrumb & { nodeId: number }).nodeId;
            categoryIsUsed = true;
          }
        });
      });
      if (!categoryIsUsed) {
        multiCategoryBreadcrumbsOptions.push([this.createCategoryBreadcrumb(category)]);
      }
    });
    return multiCategoryBreadcrumbsOptions;
  }

  createCategoryBreadcrumb(category: Record<string, Record<string, unknown>>): BreadCrumb {
    return {
      label: category['attributes']['name'] as string,
      url: `catalog/${StringUtils.toLowerCaseWithoutWhiteSpaceOrSlash(category['attributes']['name'] as string, '-')}`,
      param: '',
      breadcrumbDisabled: false,
      children: category['attributes']['children'] as [] | undefined,
      nodeId: category['attributes']['nodeId'] as number | undefined,
    };
  }

  getOneFromMultiCategoryBreadcrumbs(multiCategoryBreadcrumbsOptions: BreadCrumb[][]): BreadCrumb[] {
    const lastPage: string = LocalStorageUtils.getKeyValue('previousPage');
    for (const option of multiCategoryBreadcrumbsOptions) {
      for (const catBreadcrumb of option) {
        if (lastPage?.includes(catBreadcrumb.url)) {
          return option;
        }
      }
    }
    return multiCategoryBreadcrumbsOptions.length > 0 ? multiCategoryBreadcrumbsOptions[0] : [];
  }

  updateBreadcrumb(breadcrumbs: BreadCrumb[], route: NavigationEnd): void {
    if (this.currentRoute === route) {
      this.breadcrumbs = [...breadcrumbs];
    }
    this.breadcrumbsUpdating = false;
  }

  getHomePageForBreadcrumbs(): BreadCrumb {
    const homeRouteConfig: Route | undefined = this.router.config.find(route => route.path === ':lang/.');
    const homeParams: string[] = homeRouteConfig!.data![ERouteDataTypes.ROUTE_DATA_BREADCRUMB].split('=');
    return {
      label: homeParams[1],
      url: this.i18nService.getCurrentParam(),
      param: homeParams[0].replace(':', ''),
      breadcrumbDisabled: false,
    };
  }

  setTitleFromBreadcrumbs(): void {
    this.seoFacade.handleTitleFromBreadcrumb(this.breadcrumbs);
  }

  /*
   * Workaround due to product name containing % character #103036
   */
  breadcrumbLabelDecoded(label: string): string {
    return decodeURIComponent(label.replace(/%(?![0-9A-Fa-f][0-9A-Fa-f])/g,'$&25'));
  }
}
